import { Link, getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { REPLACEABLE, type ReplaceResult, type SearchResults } from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';
import { realmFile } from '../db/protocol.ts';
import { useReadOnly } from './readOnly.ts';

const route = getRouteApi('/_app/realm/$realmKey/search');

/** How long typing must pause before a search starts (docs/SEARCH-PLAN.md section 3.5). */
export const SEARCH_DELAY_MS = 250;
/** Replacing in more entries than this suggests exporting a backup first (docs/REPLACE-PLAN.md section 3.4). */
export const BIG_REPLACE = 20;

/** The options as address parameters: present when on, left out when off. */
export const options = (matchCase: boolean, wholeWords: boolean) => ({
  ...(matchCase ? { case: true as const } : {}),
  ...(wholeWords ? { words: true as const } : {}),
});

const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

const SKIP_REASONS: Record<ReplaceResult['skipped'][number]['reason'], string> = {
  changed: 'it changed since the search',
  gone: 'it no longer exists',
  'empty-title': 'its title would be empty',
  invalid: 'the change wasn’t allowed',
};

/**
 * Searching a realm's entries (docs/SEARCH-PLAN.md section 3.5), and replacing in them
 * (docs/REPLACE-PLAN.md section 3.5): the text, titles, aliases, labels and fields, as the
 * desktop's Find and Replace does, with Match case and Whole words. The query and options
 * live in the address, so Back, a reload or a bookmark keep them.
 */
export function SearchPage() {
  const { realmKey } = route.useParams();
  const readOnly = useReadOnly();
  const { q = '', case: matchCase = false, words: wholeWords = false } = route.useSearch();
  const navigate = useNavigate({ from: '/realm/$realmKey/search' });
  const router = useRouter();
  const file = realmFile(realmKey);
  const [typed, setTyped] = useState(q);
  const [results, setResults] = useState<SearchResults | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  /** Bumped to search again, after a replace or an undo. */
  const [again, setAgain] = useState(0);
  const latest = useRef(0);
  const pause = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Replace (docs/REPLACE-PLAN.md).
  const [replacing, setReplacing] = useState(false);
  const [replacement, setReplacement] = useState('');
  const [keepOldTitles, setKeepOldTitles] = useState(true);
  const [left, setLeft] = useState<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  const [outcome, setOutcome] = useState<{ text: string; skipped: string[]; undo: boolean } | null>(null);

  // The address can change without typing here (Back, the sidebar's link).
  useEffect(() => setTyped(q), [q]);
  // A new search starts with every entry chosen again.
  useEffect(() => {
    setLeft(new Set());
    setConfirming(false);
  }, [q, matchCase, wholeWords]);

  // A search per query; an older one still running is ignored when it answers.
  useEffect(() => {
    const text = q.trim();
    const mine = ++latest.current;
    setError('');
    if (text === '') {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    realms.search(file, { text, matchCase, wholeWords }).then(
      (found) => {
        if (mine !== latest.current) return;
        setResults(found);
        setSearching(false);
      },
      (e: unknown) => {
        if (mine !== latest.current) return;
        setError(e instanceof RealmClientError ? e.message : 'The search could not be run.');
        setSearching(false);
      },
    );
  }, [file, q, matchCase, wholeWords, again]);

  useEffect(() => () => clearTimeout(pause.current), []);

  const go = (next: { q?: string; case?: boolean; words?: boolean }) => {
    const text = next.q ?? q;
    void navigate({
      search: { ...(text ? { q: text } : {}), ...options(next.case ?? matchCase, next.words ?? wholeWords) },
      replace: true,
    });
  };

  const showing = results && q.trim() !== '' ? results : null;
  const chosen = showing ? showing.entries.filter((e) => e.replaceable > 0 && !left.has(e.entryId)) : [];
  const chosenMatches = chosen.reduce((n, e) => n + e.replaceable, 0);

  async function replace() {
    if (!showing) return;
    setWorking(true);
    setError('');
    try {
      const result = await realms.replace(
        file,
        { text: q.trim(), matchCase, wholeWords },
        replacement,
        chosen.map((e) => ({ entryId: e.entryId, version: e.version })),
        { keepOldTitles },
      );
      const titles = new Map(showing.entries.map((e) => [e.entryId, e.title]));
      setOutcome({
        text:
          result.entriesChanged === 0
            ? 'Nothing was replaced.'
            : `Replaced ${plural(result.replaced, 'match', 'matches')} in ${plural(result.entriesChanged, 'entry', 'entries')}.`,
        skipped: result.skipped.map((s) => `${s.title || titles.get(s.entryId) || 'An entry'} was skipped: ${SKIP_REASONS[s.reason]}.`),
        undo: result.entriesChanged > 0,
      });
      setConfirming(false);
      setAgain((n) => n + 1);
      void router.invalidate();
    } catch (e) {
      setError(e instanceof RealmClientError ? e.message : 'The replace could not be done.');
    } finally {
      setWorking(false);
    }
  }

  async function undo() {
    setWorking(true);
    setError('');
    try {
      await realms.undoReplace(file);
      setOutcome({ text: 'The replace was undone.', skipped: [], undo: false });
      setAgain((n) => n + 1);
      void router.invalidate();
    } catch (e) {
      setOutcome(null);
      setError(e instanceof RealmClientError ? e.message : 'The replace could not be undone.');
    } finally {
      setWorking(false);
    }
  }

  return (
    <article className="document search-page">
      <h1>Search</h1>
      <form role="search" className="search-form" onSubmit={(event) => event.preventDefault()}>
        <input
          type="search"
          aria-label="Search the realm's entries"
          placeholder="Text, titles, aliases, labels or fields"
          value={typed}
          autoFocus
          onChange={(event) => {
            const value = event.currentTarget.value;
            setTyped(value);
            clearTimeout(pause.current);
            pause.current = setTimeout(() => go({ q: value }), SEARCH_DELAY_MS);
          }}
        />
        <label className="check">
          <input type="checkbox" checked={matchCase} onChange={(event) => go({ q: typed, case: event.currentTarget.checked })} />
          Match case
        </label>
        <label className="check">
          <input type="checkbox" checked={wholeWords} onChange={(event) => go({ q: typed, words: event.currentTarget.checked })} />
          Whole words
        </label>
        {!replacing && !readOnly && (
          <button type="button" className="quiet" onClick={() => setReplacing(true)}>
            Replace…
          </button>
        )}
      </form>

      {replacing && (
        <section className="panel replace-panel" aria-labelledby="replace-heading">
          <h2 id="replace-heading">Replace</h2>
          <label>
            Replace with
            <input value={replacement} maxLength={200} onChange={(event) => setReplacement(event.currentTarget.value)} />
          </label>
          <label className="check">
            <input type="checkbox" checked={keepOldTitles} onChange={(event) => setKeepOldTitles(event.currentTarget.checked)} />
            When an entry’s title changes, keep the old title as an alias
          </label>
          <p className="meta">
            Replaces in titles, aliases, text and field values, as Realmery on the desktop does. Labels, field names and locked text
            are left alone.
          </p>
          {confirming ? (
            <div className="replace-confirm" role="alertdialog" aria-labelledby="replace-confirm-text">
              <p id="replace-confirm-text">
                Replace {plural(chosenMatches, 'match', 'matches')} of “{q.trim()}” with “{replacement}” in{' '}
                {plural(chosen.length, 'entry', 'entries')}?
                {chosen.length > BIG_REPLACE && ' That’s a lot of entries: you may want to export the realm first, from the realms page, as a backup.'}{' '}
                You can undo it until the realm next changes.
              </p>
              <div className="actions">
                <button type="button" className="primary" disabled={working} onClick={() => void replace()}>
                  {working ? 'Replacing…' : 'Replace'}
                </button>
                <button type="button" disabled={working} onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="actions">
              <button type="button" className="primary" disabled={chosen.length === 0 || searching} onClick={() => setConfirming(true)}>
                {chosen.length === 0 ? 'Nothing to replace' : `Replace in ${plural(chosen.length, 'entry', 'entries')}…`}
              </button>
              <button
                type="button"
                onClick={() => {
                  setReplacing(false);
                  setConfirming(false);
                }}
              >
                Close
              </button>
            </div>
          )}
          {showing && showing.entriesMatched > showing.entries.length && (
            <p className="meta">Only the {showing.entries.length} entries listed can be replaced in. Add words to the search to reach the rest.</p>
          )}
        </section>
      )}

      {outcome && (
        <div className="callout replace-outcome" role="status">
          <p>
            {outcome.text}{' '}
            {outcome.undo && (
              <button type="button" className="link-button" disabled={working} onClick={() => void undo()}>
                Undo
              </button>
            )}
          </p>
          {outcome.skipped.map((s) => (
            <p key={s} className="meta">
              {s}
            </p>
          ))}
        </div>
      )}

      <p className="meta search-status" role="status" aria-live="polite">
        {q.trim() === ''
          ? 'Type to search every entry’s text, title, aliases, labels and fields.'
          : searching
            ? 'Searching…'
            : results
              ? results.matches === 0
                ? 'No matches.'
                : `${plural(results.matches, 'match', 'matches')} in ${plural(results.entriesMatched, 'entry', 'entries')}.`
              : ''}
        {results && results.lockedSkipped > 0 && q.trim() !== '' && !searching && (
          <> {plural(results.lockedSkipped, 'locked entry’s text wasn’t', 'locked entries’ text wasn’t')} searched.</>
        )}
      </p>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}

      {showing && (
        <ol className="search-results" aria-busy={searching}>
          {showing.entries.map((entry) => (
            <li key={entry.entryId}>
              <h2>
                {replacing && entry.replaceable > 0 && (
                  <input
                    type="checkbox"
                    className="replace-choice"
                    aria-label={`Replace in ${entry.title}`}
                    checked={!left.has(entry.entryId)}
                    onChange={(event) => {
                      const next = new Set(left);
                      if (event.currentTarget.checked) next.delete(entry.entryId);
                      else next.add(entry.entryId);
                      setLeft(next);
                      setConfirming(false);
                    }}
                  />
                )}
                <Link
                  to="/realm/$realmKey/entry/$entryId"
                  params={{ realmKey, entryId: entry.entryId }}
                  search={{ find: q.trim(), ...options(matchCase, wholeWords) }}
                >
                  {entry.title}
                </Link>{' '}
                <span className="meta">
                  {entry.typeName} · {plural(entry.count, 'match', 'matches')}
                  {replacing && entry.replaceable === 0 && ' · only in labels or field names, which aren’t replaced'}
                </span>
              </h2>
              <ul>
                {entry.hits.map((hit, i) => {
                  // Only a chosen entry's matches show what they'd become.
                  const changes = replacing && REPLACEABLE.has(hit.field) && !left.has(entry.entryId);
                  return (
                    <li key={i}>
                      <span className="search-where">{hit.where}</span>{' '}
                      <span className="search-snippet">
                        {hit.before}
                        {changes ? (
                          <>
                            <del>{hit.text}</del>
                            <ins>{replacement}</ins>
                          </>
                        ) : (
                          <mark>{hit.text}</mark>
                        )}
                        {hit.after}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {entry.count > entry.hits.length && <p className="meta">{plural(entry.count - entry.hits.length, 'more match', 'more matches')} in this entry.</p>}
            </li>
          ))}
        </ol>
      )}
      {showing && showing.entriesMatched > showing.entries.length && (
        <p className="meta">
          Showing the first {showing.entries.length} of {plural(showing.entriesMatched, 'entry', 'entries')}. Add words to narrow the
          search.
        </p>
      )}
    </article>
  );
}
